import type { TimeEntry } from '@altyor-kronos/shared-types';

export type DayEntryDetail = {
  projectCode: string;
  projectName: string;
  designationName: string;
  userName: string;
  durationSeconds: number;
  billable: boolean;
};

export type DayDetail = {
  date: Date;
  entries: DayEntryDetail[];
  totalSeconds: number;
  entryCount: number;
};

export type DailyDetailMaps = {
  projectsById: ReadonlyMap<string, { code: string; name: string }>;
  designationNameById: ReadonlyMap<string, string>;
  usersById: ReadonlyMap<string, string>;
};

const DAY_MS = 86_400_000;

/**
 * One DayDetail per calendar day in [from, to] (local-midnight days, inclusive).
 * Index i aligns with the chart's bar i. Mirrors the day-iteration used to build
 * the chart's `dailyData`, so the two stay in lockstep.
 */
export function buildDailyDetails(
  entries: TimeEntry[],
  from: Date,
  to: Date,
  maps: DailyDetailMaps,
): DayDetail[] {
  const days: DayDetail[] = [];
  const cur = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  while (cur <= to) {
    const dayMs = cur.getTime();
    const nextMs = dayMs + DAY_MS;
    const dayEntries = entries
      .filter((e) => {
        const t = e.startTime.toDate().getTime();
        return t >= dayMs && t < nextMs;
      })
      .map((e): DayEntryDetail => {
        const proj = maps.projectsById.get(e.projectId);
        return {
          projectCode: proj?.code ?? e.projectId,
          projectName: proj?.name ?? '',
          designationName: maps.designationNameById.get(e.designationId) ?? '—',
          userName: maps.usersById.get(e.userId) ?? e.userId,
          durationSeconds: e.durationSeconds ?? 0,
          billable: e.billable,
        };
      })
      .sort((a, b) => b.durationSeconds - a.durationSeconds);
    const totalSeconds = dayEntries.reduce((s, e) => s + e.durationSeconds, 0);
    days.push({ date: new Date(dayMs), entries: dayEntries, totalSeconds, entryCount: dayEntries.length });
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}

export type DailyUserSeries = { userName: string; hours: number[] };

/**
 * One entry per user, holding that user's hours for each day (same index as
 * `details`). Feeds the stacked bar chart when several users are selected.
 * Biggest contributor first, so the stack order is stable across renders.
 */
export function buildDailyUserSeries(details: DayDetail[]): DailyUserSeries[] {
  const byUser = new Map<string, number[]>();
  details.forEach((day, dayIndex) => {
    for (const e of day.entries) {
      let hours = byUser.get(e.userName);
      if (!hours) {
        hours = new Array<number>(details.length).fill(0);
        byUser.set(e.userName, hours);
      }
      hours[dayIndex] += e.durationSeconds / 3600;
    }
  });
  const total = (hours: number[]) => hours.reduce((s, h) => s + h, 0);
  return [...byUser]
    .map(([userName, hours]) => ({ userName, hours }))
    .sort((a, b) => total(b.hours) - total(a.hours));
}

/**
 * For each day, the index of the last series holding hours — the segment that
 * tops the stack, and the only one that should print the day's total. -1 for a
 * day nobody logged on.
 */
export function topSeriesOfDay(series: DailyUserSeries[], dayCount: number): number[] {
  return Array.from({ length: dayCount }, (_, dayIndex) => {
    let top = -1;
    series.forEach((s, i) => {
      if (s.hours[dayIndex] > 0) top = i;
    });
    return top;
  });
}
